import { useMutation } from '@apollo/react-hooks';
import Button from '@atlaskit/button';
import Drawer from '@atlaskit/drawer';
import Dropdown, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import SettingsIcon from '@atlaskit/icon/glyph/settings';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import Page, { Grid, GridColumn } from '@atlaskit/page';
import PageHeader from '@atlaskit/page-header';
import { FlexContainer } from '@atlaskit/right-side-panel';
import React, { useState } from 'react';
import styled from 'styled-components';
import InstallationDetails from './InstallationDetails';
import LinkServiceDeskModal from './LinkServiceDeskModal';
import { DELETE_WIDGET } from '../../graphql/mutations';
import { WIDGETS_QUERY } from '../../graphql/queries';
import { useFlagContext } from '../../hooks/useFlagsContext';
import NotificationDetails from '../notifications/NotificationDetails';
import NotificationsTable from '../notifications/NotificationsTable';

const HeaderRow = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    align-content: center;
    padding-bottom: 30px;
    h1 {
        margin-bottom: 0;
    }
    .title {
        flex-grow: 1;
    }
    .title > div {
        margin: 0;
    }
    .button {
        flex-grow: 0;
        flex-basis: 148px;
    }
`;

const PageWrapper = styled.div`
    padding: 0 24px;
    width: 100%;
`;

const ArrowLabel = styled.div`
    padding-top: 10px;
    padding-bottom: 20px;
`;

const WidgetDescription = styled.div`
    font-size: 10px;
`;

function WidgetDetails({ widget, closePage, onAddRemoveEditors }) {
    const [isNotificationDetailsShown, setIsNotificationDetailsShown] = useState(false);
    const [isInstallationDetailsShown, setIsInstallationDetailsShown] = useState(false);
    const [isLinkServiceDeskShown, setIsLinkServiceDeskShown] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [isRemoveWidgetModalOpen, setIsRemoveModalOpen] = useState(false);
    const { pushFlag } = useFlagContext();

    // graphql
    const [deleteWidget] = useMutation(DELETE_WIDGET, {
        update(cache, { data: { deleteWidget: deleteWidgetData } }) {
            const { widgets } = cache.readQuery({ query: WIDGETS_QUERY });
            cache.writeQuery({
                query: WIDGETS_QUERY,
                data: { widgets: widgets.filter((el) => el.id !== deleteWidgetData.id) },
            });
        },
        onError(error) {
            setDeleteLoading(false);
            pushFlag('Error', error.message.replace('GraphQL error: ', ''));
        },
        onCompleted() {
            setIsRemoveModalOpen(false);
            setDeleteLoading(false);
            closePage();
            pushFlag('Success', 'Widget deleted with success!', 'success');
        },
    });

    const openNotificationDetails = (notification) => {
        setSelectedNotification(notification);
        setIsNotificationDetailsShown(true);
    };

    const openCreateDetails = () => {
        setSelectedNotification(null);
        setIsNotificationDetailsShown(true);
    };

    async function onRemoveWidget() {
        setDeleteLoading(true);
        const { id } = widget;
        await deleteWidget({ variables: { id } });
    }

    return (
        <Page>
            <Grid layout="fluid">
                <GridColumn medium={12}>
                    <FlexContainer id="attach">
                        <PageWrapper>
                            <ArrowLabel>Back to widgets list</ArrowLabel>
                            <HeaderRow>
                                <div className="title">
                                    <PageHeader>{widget.name}</PageHeader>
                                    <WidgetDescription>{widget.description}</WidgetDescription>
                                </div>
                                <Button appearance="primary" onClick={() => openCreateDetails()}>
                                    Create new notification
                                </Button>
                                <Dropdown
                                    trigger={({ triggerRef, ...props }) => (
                                        <Button
                                            {...props}
                                            iconBefore={<SettingsIcon label="Widget settings" primaryColor="#505f79" />}
                                            style={{ marginLeft: '10px' }}
                                            ref={triggerRef}
                                        >
                                            Configuration
                                        </Button>
                                    )}
                                    placement="bottom-end"
                                >
                                    <DropdownItemGroup>
                                        <DropdownItem onClick={() => setIsInstallationDetailsShown(true)}>
                                            Widget installation
                                        </DropdownItem>
                                        <DropdownItem onClick={() => onAddRemoveEditors(widget.id)}>
                                            Add/Remove Editors
                                        </DropdownItem>
                                    </DropdownItemGroup>
                                </Dropdown>
                                <Button
                                    style={{ marginLeft: '10px' }}
                                    iconBefore={<TrashIcon />}
                                    onClick={() => setIsRemoveModalOpen(true)}
                                />
                            </HeaderRow>
                            <NotificationsTable widgetId={widget.id} openDetails={openNotificationDetails} />
                        </PageWrapper>
                        <Drawer
                            isOpen={isNotificationDetailsShown}
                            onClose={() => setIsNotificationDetailsShown(false)}
                            width="full"
                        >
                            <NotificationDetails
                                notification={selectedNotification}
                                widgetId={widget.id}
                                setNotificationForUpdate={setSelectedNotification}
                                closePage={() => setIsNotificationDetailsShown(false)}
                            />
                        </Drawer>
                        <Drawer
                            isOpen={isInstallationDetailsShown}
                            onClose={() => setIsInstallationDetailsShown(false)}
                            width="full"
                        >
                            <InstallationDetails authKey={''} widgetKey={widget.widgetKey} />
                        </Drawer>
                    </FlexContainer>
                </GridColumn>
            </Grid>
            {isRemoveWidgetModalOpen && (
                <ModalTransition>
                    <Modal width={510}>
                        <ModalHeader>
                            <ModalTitle appearance="warning">Are you sure you want to delete this widget?</ModalTitle>
                        </ModalHeader>

                        <ModalBody>
                            <p>Deleting the widget can’t be taken back.</p>
                        </ModalBody>
                        <ModalFooter>
                            <Button appearance="subtle" onClick={() => setIsRemoveModalOpen(false)}>
                                Cancel
                            </Button>
                            <Button
                                autoFocus
                                appearance="warning"
                                onClick={() => onRemoveWidget()}
                                isDisabled={deleteLoading}
                            >
                                Delete
                            </Button>
                        </ModalFooter>
                    </Modal>
                </ModalTransition>
            )}
            {isLinkServiceDeskShown && <LinkServiceDeskModal setIsOpen={setIsLinkServiceDeskShown} widget={widget} />}
        </Page>
    );
}

export default WidgetDetails;
